import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import useAacFlags from "hooks/useAacFlags";
import useTypedFlags from "hooks/useTypedFlags";
import { AccountContext } from "views/AccountWrapper";
import { WorkerPool } from "types/generated";
import { hasSpaceManageAccess } from "utils/user";
import { hasManageAccess } from "utils/access";
import { AccessResult, NO_ACCESS_MESSAGE } from "utils/access";

/**
 * Hook that returns whether current user can reset worker pools
 * When both customAccessRoles and customAccessRolesV2 flags are off: uses legacy space manage access
 * When customAccessRolesV2 flag is on: uses space manage access logic (for now, will use WorkerPoolReset action later)
 * When only customAccessRoles flag is on: uses space manage access logic
 * @returns Function that takes a worker pool and returns AccessResult
 */
const useCanResetWorkerPool = (): ((workerPool: WorkerPool) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { customAccessRoles } = useAacFlags();
  const { customAccessRolesV2 } = useTypedFlags();

  return useCallback(
    (workerPool: WorkerPool): AccessResult => {
      if (viewer?.admin) {
        return { result: true };
      }

      let hasAccess: boolean;

      if (customAccessRolesV2) {
        // TODO: Replace with Action.WorkerPoolReset once it's implemented in the backend
        hasAccess = hasManageAccess(viewer, workerPool.spaceDetails.id);
      } else if (customAccessRoles) {
        hasAccess = hasManageAccess(viewer, workerPool.spaceDetails.id);
      } else {
        hasAccess = hasSpaceManageAccess(workerPool.spaceDetails.accessLevel);
      }

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [customAccessRoles, customAccessRolesV2, viewer]
  );
};

export default useCanResetWorkerPool;
